/**
 * `useOrgMembers` — the Team page's read of the organization's members over the
 * shared `ApiClient` (permissions-mgmt-api `users:list`).
 *
 * Cached via TanStack Query, keyed by the active org AND tenant: each member's
 * `roles` are the grants in the caller's current tenant (what "Manage roles"
 * adds to and removes from), so a tenant switch inside one org must refetch
 * rather than show — and diff against — the previous tenant's roles. The
 * `Invite people` dialog reuses it to detect an already-active member (skip the
 * org invite, go straight to the team add).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSession } from "@authe/ui-core/session";
import { useOrgUsersSource } from "@/api/adapters";
import type { OrgMember } from "@/api/orgUsersSource";

export interface UseOrgMembersResult {
  members: OrgMember[];
  isLoading: boolean;
  isFetching: boolean;
  error: unknown;
  refetch: () => void;
  /**
   * Remove a member / cancel their pending invitation. Resolves on success and
   * rejects with the typed `ApiError` on failure so the caller can toast it.
   */
  removeMember: (userId: string) => Promise<void>;
  /** The id currently being removed (for a per-row spinner), else `null`. */
  removingId: string | null;
  /**
   * Re-send the invitation email to a still-pending invitee. Resolves with the
   * API's `emailSent` (`false`: the invite was renewed but its email refused)
   * and rejects with the typed `ApiError` on failure so the caller can toast it.
   */
  resendInvite: (userId: string) => Promise<{ emailSent?: boolean }>;
  /** The id whose invite is currently being resent (per-row spinner), else `null`. */
  resendingId: string | null;
  /**
   * Change a member's org roles by applying an add/remove delta. Resolves on
   * success and rejects with the typed `ApiError` so the caller can surface it.
   */
  setMemberRoles: (userId: string, changes: { add: string[]; remove: string[] }) => Promise<void>;
  /** The id whose roles are currently being updated (per-row spinner), else `null`. */
  updatingRolesId: string | null;
}

/** Stable empty list — see `useOrgRoles`: a fresh `[]` per render feeds effect loops. */
const NO_MEMBERS: OrgMember[] = [];

export function useOrgMembers(options?: { enabled?: boolean; refetchInterval?: number | false }): UseOrgMembersResult {
  const source = useOrgUsersSource();
  const queryClient = useQueryClient();
  const { session } = useSession();
  const orgId = session?.tenant.orgId ?? "";
  const tenantId = session?.tenant.tenantId ?? "";

  const query = useQuery({
    // Invalidations use the `["org-users", orgId]` prefix, which covers every tenant.
    queryKey: ["org-users", orgId, tenantId, session?.tenant.customerId, session?.user?.id],
    queryFn: () => source.listMembers(),
    enabled: (options?.enabled ?? true) && Boolean(orgId),
    staleTime: 30_000,
    refetchInterval: options?.refetchInterval ?? 60_000,
  });

  const removeMutation = useMutation({
    mutationFn: (userId: string) => source.remove(userId),
    onSuccess: () => {
      // Refetch the org members so the removed / canceled row disappears.
      void queryClient.invalidateQueries({ queryKey: ["org-users", orgId] });
    },
  });

  const resendMutation = useMutation({
    mutationFn: (userId: string) => source.resend(userId),
    // No cache change: the pending row is unchanged, we only re-sent the email.
  });

  const setRolesMutation = useMutation({
    mutationFn: (vars: { userId: string; changes: { add: string[]; remove: string[] } }) =>
      source.setRoles(vars.userId, vars.changes),
    onSuccess: () => {
      // Refetch so the member's role badges reflect the new assignment set.
      void queryClient.invalidateQueries({ queryKey: ["org-users", orgId] });
    },
  });

  return {
    members: query.data ?? NO_MEMBERS,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: () => void query.refetch(),
    removeMember: (userId: string) => removeMutation.mutateAsync(userId),
    removingId: removeMutation.isPending ? (removeMutation.variables ?? null) : null,
    resendInvite: (userId: string) => resendMutation.mutateAsync(userId),
    resendingId: resendMutation.isPending ? (resendMutation.variables ?? null) : null,
    setMemberRoles: (userId, changes) => setRolesMutation.mutateAsync({ userId, changes }),
    updatingRolesId: setRolesMutation.isPending ? (setRolesMutation.variables?.userId ?? null) : null,
  };
}
